import {
  DateFormatEnum,
  ISO_COMPATIBLE_DATE,
  NOT_NUMERIC,
  TWELVE_HR_TIME,
  TWENTY_FOUR_HR_TIME,
} from '@monetize/constants';
import { Maybe } from '@monetize/types/common';
import { formatInTimeZone, toDate } from 'date-fns-tz';
import { addDays } from 'date-fns/addDays';
import { addMinutes } from 'date-fns/addMinutes';
import { addMonths } from 'date-fns/addMonths';
import { differenceInDays } from 'date-fns/differenceInDays';
import { differenceInMonths } from 'date-fns/differenceInMonths';
import { endOfMonth } from 'date-fns/endOfMonth';
import { format as formatDate } from 'date-fns/format';
import { formatDistanceToNow } from 'date-fns/formatDistanceToNow';
import { getDate } from 'date-fns/getDate';
import { getDaysInMonth } from 'date-fns/getDaysInMonth';
import { isAfter } from 'date-fns/isAfter';
import { isBefore } from 'date-fns/isBefore';
import { isLastDayOfMonth } from 'date-fns/isLastDayOfMonth';
import { isPast } from 'date-fns/isPast';
import { isSameDay } from 'date-fns/isSameDay';
import { isValid } from 'date-fns/isValid';
import { parse as parseDate } from 'date-fns/parse';
import { parseISO } from 'date-fns/parseISO';
import { setDate } from 'date-fns/setDate';
import { startOfDay } from 'date-fns/startOfDay';
import { subMinutes } from 'date-fns/subMinutes';
import isString from 'lodash/isString';
import { z } from 'zod';

export const USER_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;
export const ISO8601Format = "yyyy-MM-dd'T'HH:mm:ss'Z'";
export const ISO8601FormatWithMilliSecond = "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'";

export interface TimeZoneOption {
  abbreviation: string;
  label: string;
  displayLabel: string;
  offset: string;
  value: string;
}

/**
 * Synced globally (via global.store.ts) outside of react context to ensure that the timezone is consistent across the app
 * and so that individual components do not need concern themselves with this.
 * This is used for formatting dates in the tenant's timezone.
 */
export const TIMEZONE_CONFIG = {
  // this will be updated on tenant change
  tenantTimezone: USER_TIMEZONE,
  userTimezone: USER_TIMEZONE,
  UTC: 'UTC',
  /**
   * Initialized in useConfigDataFetcher
   * This can be used to access the full timezone information for a given timezone value
   */
  timezonesByValue: {} as Record<string, TimeZoneOption>,
};

export type TimezoneConfigKey = keyof Pick<
  typeof TIMEZONE_CONFIG,
  'tenantTimezone' | 'userTimezone' | 'UTC'
>;
const TIMEZONE_DEFAULT = 'tenantTimezone';

/**
 *  Format the given date to long (MMMM d, yyyy) and then returned the formatted result.
 *
 * @param {string | null | Date} date - The input to be formatted.
 * @returns {string} The formatted date e.g. November 30, 2021 (if valid) or empty string (if not valid)
 */
export const toDateLong = (
  date: string | null | Date,
  timezone: TimezoneConfigKey = TIMEZONE_DEFAULT,
): string => {
  return getFormattedDate(date, 'MMMM d, yyyy', TIMEZONE_CONFIG[timezone]);
};

/**
 * Format the given date to short (MMM d, yyyy) and then returned the formatted result.
 *
 * @param {string | null | Date } date - The input to be formatted.
 * @returns {string} The formatted date e.g. Nov 30, 2021 (if valid) or empty string (if not valid)
 */
export const toDateShort = (
  date: string | null | Date,
  timezone: TimezoneConfigKey = TIMEZONE_DEFAULT,
) => {
  return getFormattedDate(date, 'MMM d, yyyy', TIMEZONE_CONFIG[timezone]);
};

/**
 * Format the given date to the short (PPp) for both the date and time, and then return the formatted result.
 *
 * @param {string | null | Date } date - The input to be formatted.
 * @returns {string} The formatted date e.g. Mar 9, 2023, 6:27 AM (if valid) or empty string (if not valid)
 */
export const toDateTimeShort = (
  date: string | null | Date,
  timezone: TimezoneConfigKey = TIMEZONE_DEFAULT,
) => {
  return getFormattedDate(date, 'PPp', TIMEZONE_CONFIG[timezone]);
};

/**
 * Format the given date to only include the date (yyyy-MM-dd), and then return the formatted result.
 *
 * @param {string | null | Date | undefined} date - The input to be formatted.
 * @returns {string} The formatted date e.g. 2023-03-09 (if valid) or empty string (if not valid)
 */

export const toDateOnly = (
  date: string | null | Date,
  timezone: TimezoneConfigKey = TIMEZONE_DEFAULT,
) => {
  return getFormattedDate(date, 'yyyy-MM-dd', TIMEZONE_CONFIG[timezone]);
};

/**
 * Add the given number of days to a plain calendar date and return the result
 * as a 'yyyy-MM-dd' string. Operates purely on the calendar date (no timezone
 * conversion), so plain 'yyyy-MM-dd' inputs never shift across a day boundary.
 *
 * @param {string | Date} date - The input date (a 'yyyy-MM-dd' string or Date).
 * @param {number} days - The number of days to add (may be negative).
 * @returns {string} The resulting date e.g. '2026-06-01' (if valid) or '' (if not valid)
 */
export const addDaysToDateOnly = (
  date: string | Date,
  days: number,
): string => {
  const parsed = isString(date) ? parseISO(date) : date;
  if (!isValid(parsed)) {
    return '';
  }
  return formatDate(addDays(parsed, days), 'yyyy-MM-dd');
};

/**
 * Net terms enum values follow the `NET_<days>` convention (e.g. NET_30).
 * Returns the numeric day count, or 0 for values without a parseable day count
 * (e.g. NET_NEW) or when net terms is absent.
 */
export const getNetTermsDays = (netTerms?: string | null): number => {
  if (!netTerms) {
    return 0;
  }
  const days = Number.parseInt(String(netTerms).replace('NET_', ''), 10);
  return Number.isFinite(days) ? days : 0;
};
/**
 * Format the given date to only include the date in MM-dd-yyyy format, and then return the formatted result.
 *
 * @param {string | null | Date} date - The input to be formatted.
 * @returns {string} The formatted date e.g. 03-09-2023 (if valid) or empty string (if not valid)
 */
export const toDateLocal = (
  date: string | null | Date,
  timezone: TimezoneConfigKey = TIMEZONE_DEFAULT,
) => {
  return getFormattedDate(date, 'MM-dd-yyyy', TIMEZONE_CONFIG[timezone]);
};
/**
 * Format the given date to a string in the format of 'MM/dd/yyyy'.
 *
 * @param {string | null | Date} date - The date to be formatted.
 * @returns {string} The formatted date e.g. 03/09/2023 (if valid) or empty string (if not valid)
 */

export const toDateSlash = (
  date: string | null | Date,
  timezone: TimezoneConfigKey = TIMEZONE_DEFAULT,
) => {
  return getFormattedDate(date, 'MM/dd/yyyy', TIMEZONE_CONFIG[timezone]);
};
/**
 * Format the given date to a string of 'MM/dd/yyyy, h:mm a' format.
 *
 * @param {string | null | Date} date - The date to be formatted.
 * @returns {string}  The formatted date e.g.03/09/2023, 6:27 AM (if valid) or empty string (if not valid)
 */
export const toDateSlashTime = (
  date: string | null | Date,
  timezone: TimezoneConfigKey = TIMEZONE_DEFAULT,
) => {
  return getFormattedDate(
    date,
    'MM/dd/yyyy, h:mm a',
    TIMEZONE_CONFIG[timezone],
  );
};

/**
 * Format the given date to a time string of 'h:mm aaa' format.
 * @param {string | null | Date} date - The date to format as a time string.
 * @returns {string} The formatted date e.g. 6:27 AM (if valid) or empty string (if not valid)
 */
export const toTime = (
  date: string | null | Date,
  timezone: TimezoneConfigKey = TIMEZONE_DEFAULT,
) => {
  return getFormattedDate(date, 'h:mm aaa', TIMEZONE_CONFIG[timezone]);
};

/**
 * Format the given date string in the "MMM d, yyyy" format.
 * @param {string | null} date - The date string to format.
 * @returns {string} The formatted date string.
 */
export const toDateMMMDDYYYY = (
  date: string | null | Date,
  timezone: TimezoneConfigKey = TIMEZONE_DEFAULT,
) => {
  return getFormattedDate(date, 'MMM d, yyyy', TIMEZONE_CONFIG[timezone]);
};

/**
 * Takes a Zod string schema and returns a refined schema that validates if the
 * provided input is a valid date. If input is null or undefined, it always returns true.
 * If input is a string, it tries to parse it using the provided date format and returns true if the resulting date is
 * valid.
 * If the input is not valid, it throws an error with the message "Incorrect date format".
 * @param {z.ZodString} zod - The Zod string schema to refine.
 * @returns {z.ZodEffects<z.ZodString, string, string>} - A refined Zod string schema that validates if the input is
 * a valid date string
 */
export const formDateStringZodSchema = (zod: z.ZodString) => {
  return zod.refine((val: string | null) => {
    if (!val || val.trim() === '') {
      return true;
    }
    return isValidDate(parseISO(val));
  }, 'Incorrect date format');
};

/**
 * Parse datetime from a string - this can be used for parsing dates from Excel or Google Sheets etc..
 * First checks for an ISO compliant string and uses that if provided
 * Otherwise, the format will be attempted to be detected and parsed
 *
 * Example inputs that are accepted (these are generated from programs like Excel and Google Sheets)
 * 12/12/2020 15:15:00
 * 1/3/2023 13:44:19
 * 1/3/23 1:44 PM
 */
export const parseDatetime = (value: string | null | Date): Date | null => {
  if (!value) {
    return null;
  }
  if (value instanceof Date) {
    return value;
  }
  if (isString(value)) {
    value = value.trim();
    if (ISO_COMPATIBLE_DATE.test(value)) {
      return parseISO(value);
    }
    /**
     * Date will be in a format like this:
     * 1/3/2023 13:44:19 OR 1/3/23 1:44 PM
     */
    value = value.replace('T', ' '); // just in case a T is used for the timezone instead of a space
    const dateParts = value.split(' ');
    // eslint-disable-next-line prefer-destructuring
    const date = dateParts[0];
    const time = dateParts.slice(1).join(' ');
    // TODO: support other locales where date is formatted differently
    let outputDate = ISO_COMPATIBLE_DATE.test(date)
      ? parseISO(date)
      : buildDateFromString(date, DateFormatEnum.MM_DD_YYYY);
    if (outputDate) {
      if (TWENTY_FOUR_HR_TIME.test(time)) {
        outputDate = parseDate(time, 'H:m:ss', outputDate);
      } else if (TWELVE_HR_TIME.test(time)) {
        outputDate = parseDate(time, 'h:m a', outputDate);
      }
      if (isValid(outputDate)) {
        return outputDate;
      } else {
        // logger.warn('Unable to parse date', value);
      }
    }
  }
  return null;
};

/**
 * Given a string that is formatted in a non-ISO date format, return a Date object
 *
 * Example input:
 * 12/12/2020
 * 1/3/2023
 * 1/3/23
 *
 * @param value
 * @param dateFormat locale of the date format
 * @returns
 */
export const buildDateFromString = (
  value: string,
  dateFormat: DateFormatEnum,
): Date | null => {
  const refDate = startOfDay(new Date());
  const tempValue = value.replace(NOT_NUMERIC, '-'); // FIXME: some date formats are 'd. m. yyyy' like 'sk-SK'
  let [first, middle, end] = tempValue.split('-');
  if (!first || !middle || !end) {
    return null;
  }
  switch (dateFormat) {
    case DateFormatEnum.MM_DD_YYYY: {
      first = first.padStart(2, '0');
      middle = middle.padStart(2, '0');
      end = end.padStart(4, '20');
      return parseDate(`${first}-${middle}-${end}`, 'MM-dd-yyyy', refDate);
    }
    case DateFormatEnum.DD_MM_YYYY: {
      first = first.padStart(2, '0');
      middle = middle.padStart(2, '0');
      end = end.padStart(4, '20');
      return parseDate(`${first}-${middle}-${end}`, 'dd-MM-yyyy', refDate);
    }
    case DateFormatEnum.YYYY_MM_DD: {
      first = first.padStart(4, '20');
      middle = middle.padStart(2, '0');
      end = end.padStart(2, '0');
      return parseDate(`${first}-${middle}-${end}`, 'yyyy-MM-dd', refDate);
    }
    default:
      throw new Error('Invalid date format');
  }
};
/**
 * Formats a date string as a human-readable string representing the time elapsed since the input date.
 * @param {string | null | Date} date - The date string to format.
 * @returns {string} The human-readable string representing the time elapsed since the input date.
 */
export const fromNow = (date: string | null | Date) => {
  if (date === null) {
    return '';
  }
  const parsedDate = date instanceof Date ? date : parseISO(date);
  return isValidDate(parsedDate)
    ? formatDistanceToNow(parsedDate, { addSuffix: true })
    : '';
};
/**
 * Checks if a given value is a valid date string or Date object.
 * @summary
 * wrapper function for `date-fns`'s `isValid`
 * @param {Date | string | null | undefined} date The date or date string to validate.
 * @returns {boolean} Whether the input is a valid date or not.
 */

export const isValidDate = (
  date: Date | string | null | undefined,
): date is Date => {
  return date instanceof Date && isValid(date);
};

/**
 * Format date using the tenant's timezone as the default.
 *
 * For date fields, this will default to user browser timezone to ensure no date skew
 *
 * If a date is provided (not string) and the value should be a date (not datetime) then caller needs to set to userTimezone to avoid date skew
 */
export const getFormattedDate = (
  date: string | null | Date,
  format: string,
  timezone = TIMEZONE_CONFIG.tenantTimezone,
): string => {
  const isDateWithoutTimeZone = isString(date) && date.length === 10;
  let dateToFormat: Date | null = null;

  if (isValidDate(date)) {
    dateToFormat = date; // typescript knows 100% that date is type Date
  } else if (isString(date) && date.trim() !== '') {
    dateToFormat = parseISO(date);
  }

  if (!dateToFormat || !isValid(dateToFormat)) {
    return '';
  }
  // If there is no timezone, then we don't want to modify the date
  if (isDateWithoutTimeZone) {
    return formatDate(dateToFormat, format);
  }

  try {
    return formatInTimeZone(dateToFormat, timezone, format);
  } catch (error) {
    // FIXME:
    // Sentry.captureException(
    //   {
    //     message: 'Error formatting date',
    //     extra: {
    //       date,
    //       dateToFormat,
    //       format,
    //       timezone,
    //     },
    //   },
    //   {
    //     tags: {
    //       type: 'DATE_FORMATTING',
    //     },
    //   },
    // );
    // logger.error('Error formatting date:', error);
    return dateToFormat.toString();
  }
};

/**
 * Returns the date in the UTC timezone in the given format.
 */
export const getFormattedDateUtc = (
  date: string | null | Date,
  format: string,
): string => {
  return getFormattedDate(date, format, 'UTC');
};

/**
 * Ensures that the input value is a valid Date object. If the input value is null, undefined, or not a valid Date object or string,
 * it returns the provided fallback value.
 *
 * @param {string | Date | null | undefined} date - The value to ensure is a valid Date object or string.
 * @param {Date} fallback - The fallback value to use if the input value is not a valid Date object or string.
 * @returns {Date} A valid Date object.
 */
export const ensureDate = (
  date: Maybe<string | Date>,
  fallback = new Date(),
): Date => {
  if (isValidDate(date)) {
    return date;
  } else if (isString(date) && date.trim() !== '') {
    const dateToFormat = parseISO(date);
    return isValid(dateToFormat) ? dateToFormat : fallback;
  }
  return fallback;
};

/**
 * Takes an ISO date string (e.x. 05-03-2023) and parses it using UTC timezone
 */
export const parseDateAsUtc = (date: string): Date => {
  if (!date || date.length !== 10) {
    // not sure what to return - but this is not a valid date
  }
  return parseISO(`${date}T00:00:00.000Z`);
};

export const parseDateAsLocalTime = (date: string): Date => {
  return new Date(`${date}T00:00:00`);
};

/**
 * Takes a date-only value (e.x. 2023-05-03) and returns the ISO timestamp for noon UTC on that day.
 *
 * Noon is deliberate: the instant resolves to the picked calendar day in UTC - which is how the backend reads every
 * timestamp back into a date - and holds that day when rendered anywhere from UTC-12 to UTC+11. Zones at UTC+12 and
 * beyond (New Zealand, Kiribati) tip into the following day; no single instant can cover the 26 hours of offsets at
 * once, and matching the backend's UTC reading is what keeps a picked day and a derived contract date in agreement.
 */
export const toNoonUtcTimestamp = (date: string): string =>
  `${date.substring(0, 10)}T12:00:00.000Z`;

/**
 * This is used in places where the user is entering a date/time in UTC
 * instead of their local timezone. The timezone offset is removed before parsing
 * and is coerced to UTC
 *
 * @param date
 * @returns
 */
export const replaceUserTimezoneWithUtc = (date: string): Date => {
  return toDate(date.substring(0, 19), { timeZone: 'Etc/UTC' });
};

export const adjustTimeZoneOffsetWithUtc = (date: Date): Date => {
  const timeZoneOffset = date.getTimezoneOffset();
  return Math.sign(timeZoneOffset) === -1
    ? subMinutes(date, Math.abs(timeZoneOffset))
    : addMinutes(date, timeZoneOffset);
};

export const sortByDate = (a: string, b: string) => {
  if (isValidDate(parseISO(a)) && isValidDate(parseISO(b))) {
    return new Date(a).valueOf() - new Date(b).valueOf();
  }
  return 0;
};

export const isBetween = (date: Date, startDate: Date, endDate: Date) => {
  return isBefore(date, endDate) && isAfter(date, startDate);
};

export const isBetweenInclusive = (
  date: Date,
  startDate: Date,
  endDate: Date,
) => {
  return (
    (isBefore(date, endDate) || isSameDay(date, endDate)) &&
    (isAfter(date, startDate) || isSameDay(date, startDate))
  );
};

/**
 * If the starting date is the last day of the month, then add months will lock to the last day of the month
 *
 * Important Note:
 * Billing platform calculates billing periods using the same day of the month, NOT locking to the last day of the month
 *
 * For any usage where we are calculating dates to align to billing periods, **DO NOT** use this function and use addMonths from date-fns directly instead.
 *
 * @example
 * Feb 28 + 1 month = Mar 30 (instead of Mar 28)
 */
export const addMonthsCustom = (date: Date, months: number) => {
  if (isLastDayOfMonth(date)) {
    return endOfMonth(addMonths(date, months));
  }
  return addMonths(date, months);
};

/**
 * Returns a date that is the next occurrence of the same day of the month as the reference date.
 * If the calculated date is in the past, it will add another month and recalculate.
 * If the day doesn't exist in the month, it will return the end of the month.
 *
 * This is used for snapping an offering start date to the contract start date
 *
 * @param dayOfMonth The day of the month that we want to target (e.g. contract start date day of month)
 * @returns
 */
export const getNextOccurrenceSameDayOfMonth = (dayOfMonth: number): Date => {
  // start with today and then calculate the next occurrence of the same day of the month from the referenceDate
  let nextDate = new Date();

  // date-fns setDate does not throw for out-of-range days — it overflows into the
  // next month. Explicitly check the day count to return end-of-month instead.
  if (dayOfMonth > getDaysInMonth(nextDate)) {
    nextDate = endOfMonth(nextDate);
  } else {
    nextDate = setDate(nextDate, dayOfMonth);
  }

  // If the calculated date is in the past, add another month and recalculate
  if (isPast(nextDate)) {
    nextDate = addMonths(nextDate, 1);
    if (dayOfMonth > getDaysInMonth(nextDate)) {
      nextDate = endOfMonth(nextDate);
    } else {
      nextDate = setDate(nextDate, dayOfMonth);
    }
  }

  return nextDate;
};

/**
 * Returns the relevant day-of-month within the same month as the reference date.
 * If dayOfMonth doesn't exist (e.g., Feb 31), returns todays date.
 *
 * @param dayOfMonth Desired day (e.g. 31)
 * @param referenceDate Date whose month we are targeting
 * @returns Date
 */
export const getRelevantDayInMonth = (
  dayOfMonth: number,
  referenceDate: Date,
): Date => {
  const result = setDate(referenceDate, dayOfMonth);

  if (isValid(result) && getDate(result) === dayOfMonth) {
    return result;
  }
  const todaysDate = new Date();

  return todaysDate;
};

/**
 * Calculate the difference between two dates in months and days.
 * Returns the number of complete months and remaining days.
 * The end date is treated as inclusive (e.g., Dec 1, 2025 to Nov 30, 2026 = 12 months).
 *
 * @param startDate The start date (string or Date)
 * @param endDate The end date (string or Date) - inclusive
 * @returns Object with months, days, and humanized string
 */
export const calculateDateDifference = (
  startDate: string | Date,
  endDate: string | Date,
): { months: number; days: number; humanized: string } => {
  const startDay = startOfDay(
    typeof startDate === 'string' ? parseISO(startDate) : startDate,
  );
  const endDay = startOfDay(
    typeof endDate === 'string' ? parseISO(endDate) : endDate,
  );

  if (!isValid(startDay) || !isValid(endDay)) {
    return { months: 0, days: 0, humanized: '' };
  }

  // Add 1 day to endDate to make it inclusive for calculation
  const inclusiveEndDay = addDays(endDay, 1);

  // Calculate complete months
  let months = differenceInMonths(inclusiveEndDay, startDay);
  let days = 0;

  if (isSameDay(addMonths(startDay, months + 1), inclusiveEndDay)) {
    months = months + 1;
    days = 0;
  } else {
    days = differenceInDays(inclusiveEndDay, addMonths(startDay, months));
  }

  // Build humanized string
  const parts: string[] = [];
  if (months > 0) {
    parts.push(`${months} ${months === 1 ? 'month' : 'months'}`);
  }
  if (days > 0) {
    parts.push(`${days} ${days === 1 ? 'day' : 'days'}`);
  }

  const humanized = parts.length > 0 ? parts.join(' ') : '0 days';

  return { months, days, humanized };
};
